'use no memo'

import {Editor} from '@tiptap/core'
import {EditorContent, useEditor} from '@tiptap/react'
import clsx from 'clsx'
import {FC, useEffect, useRef, useState} from 'react'

import {RichTextToolbar} from './RichTextToolbar'
import {buildRichTextExtensions} from './extensions'
import {isEmptyRichText, normalizeLegacyRichText} from './legacyHtml'

export type RichTextEditorProps = {
  value?: string
  onChange?: (html: string) => void
  onEditorReady?: (editor: Editor) => void
  placeholder?: string
  readOnly?: boolean
  acceptImage?: boolean
  className?: string
  id?: string
  onKeyDown?: (event: React.KeyboardEvent) => void
  onBlur?: (event: React.FocusEvent) => void
}

/**
 * TipTap-based rich text editor. Emits clean semantic HTML (real ul/ol,
 * inline styles for align/size/color) that renders in any consumer without
 * editor CSS — unlike the previous Quill editor. Legacy Quill markup in the
 * incoming value is normalized on load via normalizeLegacyRichText.
 */
export const RichTextEditor: FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  onEditorReady,
  placeholder,
  readOnly = false,
  acceptImage = true,
  className,
  id,
  onKeyDown,
  onBlur,
}) => {
  const [codeView, setCodeView] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [codeHtml, setCodeHtml] = useState('')
  // Last value this editor emitted; lets the external-value sync effect
  // ignore its own updates echoed back through form state.
  const lastEmittedRef = useRef<string | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const emit = (html: string) => {
    const clean = isEmptyRichText(html) ? '' : html
    lastEmittedRef.current = clean
    onChangeRef.current?.(clean)
  }

  const editor = useEditor({
    extensions: buildRichTextExtensions(placeholder),
    content: normalizeLegacyRichText(value),
    editable: !readOnly,
    onUpdate: ({editor: e}) => emit(e.getHTML()),
  })

  useEffect(() => {
    if (editor && !editor.isDestroyed && onEditorReady) onEditorReady(editor)
  }, [editor, onEditorReady])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!readOnly)
  }, [editor, readOnly])

  // Fullscreen writing mode: lock page scroll behind it, exit with Escape.
  useEffect(() => {
    if (!fullscreen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [fullscreen])

  // Sync external value changes (form resets, async prefills) into the editor
  // without fighting the user's own typing. The isDestroyed guard matters:
  // when a Suspense boundary hides and re-shows this subtree, reconnected
  // effects close over an editor useEditor has already destroyed, and
  // getHTML() on a destroyed editor throws (schema is nulled on destroy).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (value === lastEmittedRef.current) return
    const normalized = normalizeLegacyRichText(value)
    if (normalized === editor.getHTML()) return
    editor.commands.setContent(normalized, {emitUpdate: false})
    lastEmittedRef.current = value
  }, [editor, value])

  const toggleCodeView = () => {
    if (!editor || editor.isDestroyed) return
    if (!codeView) {
      setCodeHtml(editor.getHTML())
      setCodeView(true)
    } else {
      editor.commands.setContent(normalizeLegacyRichText(codeHtml), {emitUpdate: false})
      emit(editor.getHTML())
      setCodeView(false)
    }
  }

  if (!editor) return null

  return (
    <div
      id={id}
      className={clsx('rich-text-editor', className, {
        'rte-fullscreen': fullscreen,
        'rte-readonly': readOnly,
      })}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      {!readOnly && (
        <RichTextToolbar
          editor={editor}
          acceptImage={acceptImage}
          codeView={codeView}
          onToggleCodeView={toggleCodeView}
          fullscreen={fullscreen}
          onToggleFullscreen={() => setFullscreen((v) => !v)}
        />
      )}
      {codeView ? (
        <textarea
          className='rte-codeview form-control'
          value={codeHtml}
          spellCheck={false}
          onChange={(e) => setCodeHtml(e.target.value)}
        />
      ) : (
        <EditorContent editor={editor} className='rte-content' />
      )}
    </div>
  )
}
